<template>
    <p-modal
        name="posDeviceReconciliationModal"
        label="POS Device Reconciliation"
        ref="posDeviceReconciliation"
        size="lg"
        @ok.prevent="handleModalOk"
    >
        <template v-slot:default>
            <p-form ref="posDeviceReconciliationForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Sales by POS Device by Tender</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select
                                    data-type="posDevicesWithLocation"
                                    rules="required"
                                    :default-selected="true"
                                    :initial-display-count="10"
                                    v-model="reportParameters.registerIds"
                                />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Tender Status">
                                <b-row>
                                    <b-col>
                                        <b-form-radio
                                            :value="true"
                                            v-model="
                                                reportParameters.tenderStatusOption
                                            "
                                        >
                                            Active Tenders Only
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio
                                            :value="false"
                                            v-model="
                                                reportParameters.tenderStatusOption
                                            "
                                        >
                                            All
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                            <p-card title="Export Format">
                                <p-checkbox size="md"
                                    label="PDF"
                                    v-model="exportFormats.pdf" />
                                <p-checkbox size="md"
                                    label="CSV"
                                    v-model="exportFormats.csv" />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2">
                        Export
                    </p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

const reportName = 'Pos Device Reconciliation';

export default {
    mixins: [ReportTemplates],
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'POS Device Reconciliation',
            disabled: true,
            exportFormats: {
                pdf: true,
                csv: false
            },
            reportParameters: {
                registerIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                tenderStatusOption: true,
                locationId: null
            }
        };
    },
    computed: {
        pageTitle() {
            return reportName;
        },
        hasExportFormat() {
            return this.exportFormats.pdf || this.exportFormats.csv;
        }
    },
    methods: {
        async show() {
            this.exportFormats.pdf = true;
            this.exportFormats.csv = false;

            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            this.reportParameters.registerIds = (posDevices || []).map(x => x.value);
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.posDeviceReconciliation.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The POS device ids are the only saved
        // values tied to changing data — a disabled or deleted register drops out of the list. The
        // dates and the tender status radio are literal values, not references.
        async validateReportParameters(savedParameters) {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation') ?? [];

            return (savedParameters.registerIds ?? []).every(registerId =>
                posDevices.some(posDevice => posDevice.value == registerId));
        },
        handleModalOk: async function() {
            if (!(await this.$refs.posDeviceReconciliationForm.validate())) {
                return;
            }

            if (this.reportParameters.registerIds.length === 0) {
                this.$appError(
                    'Please select one or more Pos Devices.'
                );
                return;
            }

            if (!this.hasExportFormat) {
                this.$appError(
                    'Please choose at least one export format, PDF or CSV.'
                );
                return;
            }

            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            try {
                if (this.exportFormats.pdf) {
                    const pdf = await axios.post('/sales/exportPosDeviceReconciliation', this.reportParameters, {
                        responseType: 'blob'
                    });

                    downloadFileToBrowser(pdf.data, `${reportName}.pdf`, 'application/pdf');
                }

                if (this.exportFormats.csv) {
                    const csv = await axios.post('/sales/exportPosDeviceReconciliationCsv', this.reportParameters, {
                        responseType: 'blob'
                    });

                    downloadFileToBrowser(csv.data, `${reportName}.csv`, 'application/csv');
                }

                this.$appSuccess(`${reportName} report generated successfully.`);
                this.$refs.posDeviceReconciliation.hide();
            } finally {
                this.isBusy = false;
            }
        }
    }
};
</script>

<style scoped>
.static-height {
    height:370px
}
</style>
